import React, { useState } from 'react'
import { Button, Checkbox, Flex, Form, Modal, notification } from 'antd'
import { AntdForm } from '@/common/components/AntdForm'
import { useImpersonateUserMutation } from '@/features/auth/auth.api.ts'
import { handleApiError } from '@/utils/api.tsx'
import { useEmployeePublicInfo } from '@/common/hooks/useEmployeePublicInfo.tsx'
import { ValidatorRule } from 'rc-field-form/lib/interface'
import { AntdFormRules } from '@/common/components/AntdForm/rules.tsx'
import { AppEnv } from '@/utils/env.ts'

type FormValues = {
  username: string
}

export const ImpersonateUserModal: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [needValidate, setNeedValidate] = useState(true)
  const [impersonateUser, { isLoading }] = useImpersonateUserMutation()
  const { getEmployeePublicInfoByUsername } = useEmployeePublicInfo()
  const [form] = Form.useForm()

  const showDrawer = () => {
    setOpen(true)
  }

  const onFinish = async (data: FormValues) => {
    const result = await impersonateUser({
      envName: AppEnv.env,
      username: data.username,
    })
    if ('data' in result) {
      notification.success({ message: `Impersonate as ${data.username} successfully` })
      form.resetFields()
      setOpen(false)
      location.reload()
    } else {
      handleApiError(result.error, 'Impersonation failed')
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setNeedValidate(true)
    setOpen(false)
  }

  const validateUsername: ValidatorRule['validator'] = (_, value?: string) => {
    if (!value) {
      return Promise.resolve()
    }
    const employee = getEmployeePublicInfoByUsername(value)
    if (!employee) {
      return Promise.reject(new Error('Username does not exist'))
    }
    return Promise.resolve()
  }

  return (
    <>
      <Button onClick={showDrawer}>Impersonate User</Button>
      <Modal
        open={open}
        title="Impersonate User"
        destroyOnHidden
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          onFinish={onFinish}
        >
          {needValidate ? (
            <AntdForm.EmployeeSearch
              getUsername
              fieldProps={{
                name: 'username',
                rules: [AntdFormRules.required, ...(needValidate ? [{ validator: validateUsername }] : [])],
                validateDebounce: 1000,
              }}
            />
          ) : (
            <AntdForm.Input
              allowClear
              fieldProps={{
                name: 'username',
                rules: [AntdFormRules.required, ...(needValidate ? [{ validator: validateUsername }] : [])],
                validateDebounce: 1000,
              }}
            />
          )}
          <Checkbox checked={needValidate} onChange={e => setNeedValidate(e.target.checked)}>
            Validate username
          </Checkbox>
          <Flex gap="small" justify="flex-end">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>Confirm</Button>
          </Flex>
        </Form>
      </Modal>
    </>
  )
}